import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "design-form" }
const _hoisted_2 = { class: "design-form__group" }
const _hoisted_3 = {
  for: "designName",
  class: "design-form__label"
}
const _hoisted_4 = ["placeholder"]
const _hoisted_5 = {
  key: 0,
  class: "design-form__error"
}
const _hoisted_6 = {
  key: 0,
  class: "design-form__general-error"
}
const _hoisted_7 = { class: "design-form__actions" }

import { ref, computed } from 'vue';
// @ts-ignore
import { watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AppModal from '../../base/Modal.vue';
import AppButton from '../../base/AppButton.vue';
import DesignService from '@/services/design.service';
import type { DesignCreationResponse } from '@/services/design.service';

interface Props {
  show: boolean;
  projectId: string | number;
}

interface Emits {
  (e: 'close'): void;
  (e: 'created', design: DesignCreationResponse): void;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'NewDesignModal',
  props: {
    show: { type: Boolean, default: false },
    projectId: {}
  },
  emits: ["close", "created"],
  setup(__props: any, { emit: __emit }) {

/// <reference types="vue" />
// @ts-ignore
const { t } = useI18n();

const props = __props;

const emit = __emit;

// Form data
const formData = ref({
  name: ''
});

// Form state
const saving = ref<boolean>(false);
const errors = ref<Record<string, string>>({});

// Computed properties
const isFormValid = computed(() => {
  return formData.value.name?.trim() &&
         Object.keys(errors.value).length === 0;
});

// Methods
const validateName = (): void => {
  if (errors.value.name) {
    validateForm();
  }
};

const validateForm = (): boolean => {
  const newErrors: Record<string, string> = {};

  // Name validation
  if (!formData.value.name?.trim()) {
    newErrors.name = t('design.nameRequired');
  } else if (formData.value.name.trim().length < 2) {
    newErrors.name = t('design.nameMinLength');
  } else if (formData.value.name.trim().length > 100) {
    newErrors.name = t('design.nameMaxLength');
  }

  errors.value = newErrors;
  return Object.keys(newErrors).length === 0;
};

const resetForm = (): void => {
  formData.value = {
    name: ''
  };
  errors.value = {};
};

const handleSubmit = async (): Promise<void> => {
  // Prevent duplicate submissions
  if (saving.value) {
    return;
  }

  if (!validateForm()) {
    return;
  }

  try {
    saving.value = true;

    const createdDesign = await DesignService.createDesign(props.projectId.toString(), formData.value.name.trim());

    emit('created', createdDesign);
    resetForm();

  } catch (error: any) {
    console.error('Error creating design:', error);

    // Handle specific backend errors
    if (error.response && error.response.data) {
      const responseData = error.response.data;

      // Handle business errors (like name already exists)
      if (responseData.businessError === 'DESIGN_NAME_ALREADY_EXISTS') {
        errors.value.name = t('design.nameExists');
      } else if (responseData.fieldErrors) {
        // Handle validation errors from server
        const serverErrors: Record<string, string> = {};
        responseData.fieldErrors.forEach((fieldError: any) => {
          serverErrors[fieldError.field] = fieldError.message;
        });
        errors.value = { ...errors.value, ...serverErrors };
      } else {
        // Generic server error
        errors.value.general = responseData.message || t('design.createError');
      }
    } else {
      // Network or other errors
      errors.value.general = error.message || t('design.createError');
    }
  } finally {
    saving.value = false;
  }
};

// Watch for modal visibility to reset form
watch(() => props.show, (newShow: boolean) => {
  if (newShow) {
    resetForm();
  }
});

// Real-time validation
watch(() => formData.value.name, () => {
  if (errors.value.name || errors.value.general) {
    // Clear general errors when user starts typing
    delete errors.value.general;

    // Re-validate name if there was an error
    if (errors.value.name) {
      validateForm();
    }
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(AppModal, {
    show: _ctx.show,
    onClose: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('close'))),
    title: _ctx.$t('design.newDesign'),
    icon: "plus",
    size: "medium",
    loading: saving.value
  }, {
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_7, [
        _createVNode(AppButton, {
          onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('close'))),
          variant: "secondary"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('common.cancel')), 1)
          ]),
          _: 1
        }),
        _createVNode(AppButton, {
          onClick: handleSubmit,
          variant: "primary",
          loading: saving.value,
          disabled: saving.value || !isFormValid.value
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(saving.value ? _ctx.$t('common.saving') : _ctx.$t('design.createDesign')), 1)
          ]),
          _: 1
        }, 8, ["loading", "disabled"])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("label", _hoisted_3, [
            _createTextVNode(_toDisplayString(_ctx.$t('design.designName')) + " ", 1),
            _cache[3] || (_cache[3] = _createElementVNode("span", { class: "design-form__required" }, "*", -1))
          ]),
          _withDirectives(_createElementVNode("input", {
            id: "designName",
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.value.name) = $event)),
            type: "text",
            required: "",
            class: _normalizeClass(["design-form__input", { 'design-form__input--error': errors.value.name }]),
            placeholder: _ctx.$t('design.designNamePlaceholder'),
            maxlength: "100",
            onBlur: validateName
          }, null, 42, _hoisted_4), [
            [_vModelText, formData.value.name]
          ]),
          (errors.value.name)
            ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(errors.value.name), 1))
            : _createCommentVNode("", true)
        ]),
        (errors.value.general)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _cache[4] || (_cache[4] = _createElementVNode("svg", {
                viewBox: "0 0 24 24",
                width: "16",
                height: "16"
              }, [
                _createElementVNode("path", {
                  fill: "currentColor",
                  d: "M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z"
                })
              ], -1)),
              _createTextVNode(" " + _toDisplayString(errors.value.general), 1)
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["show", "title", "loading"]))
}
}

})